import { Sparkles, Handshake, Building2, GraduationCap, Globe, TrendingUp, Users, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

const Partnership = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    partnershipType: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.partnershipType) {
      toast({
        title: "필수 정보 누락",
        description: "모든 필수 항목을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "파트너십 문의 접수 완료",
      description: "담당자가 검토 후 연락드리겠습니다.",
    });

    setFormData({
      name: "",
      email: "",
      company: "",
      partnershipType: "",
      message: "",
    });
  };

  const partnershipTypes = [
    {
      icon: Building2,
      title: "기업 파트너",
      description: "AI 리터러시 진단 서비스를 공동으로 제공하는 전략적 파트너십",
      benefits: [
        "공동 마케팅 및 브랜딩",
        "수익 배분 모델",
        "기술 지원 및 교육",
        "우선 고객 소개",
      ],
      color: "blue",
    },
    {
      icon: GraduationCap,
      title: "교육 기관",
      description: "대학, 연구소 등 교육 기관과의 학술 협력 파트너십",
      benefits: [
        "공동 연구 프로젝트",
        "교육 프로그램 개발",
        "학술 자료 공유",
        "인턴십 프로그램",
      ],
      color: "purple",
    },
    {
      icon: Globe,
      title: "글로벌 파트너",
      description: "해외 시장 진출을 위한 국제 파트너십",
      benefits: [
        "현지 시장 진출 지원",
        "문화적 적응 컨설팅",
        "글로벌 네트워크 구축",
        "다국어 자료 제공",
      ],
      color: "green",
    },
    {
      icon: TrendingUp,
      title: "기술 파트너",
      description: "AI 기술 및 플랫폼 제공 파트너십",
      benefits: [
        "기술 통합 지원",
        "API 연동",
        "공동 제품 개발",
        "기술 라이선스",
      ],
      color: "orange",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-xl font-bold">KAILA</span>
                <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/")}>홈</Button>
              <Button variant="ghost" onClick={() => navigate("/about")}>소개</Button>
              <Button variant="ghost" onClick={() => navigate("/methodology")}>방법론</Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/consultation')}
              >
                상담신청
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 dark:from-blue-500/5 dark:via-transparent dark:to-purple-500/5" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-500 text-white">
              <Handshake className="h-4 w-4 mr-2 inline" />
              파트너십
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              함께 성장하는<br />파트너십
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              KAILA와 함께 AI 리터러시 생태계를 만들어갑니다
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">파트너십 유형</h2>
              <p className="text-muted-foreground">다양한 형태의 협력 모델을 제공합니다</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {partnershipTypes.map((type, index) => {
                const Icon = type.icon;
                const colorClasses = {
                  blue: "from-blue-500 to-blue-600 border-blue-500/20",
                  purple: "from-purple-500 to-purple-600 border-purple-500/20",
                  green: "from-green-500 to-green-600 border-green-500/20",
                  orange: "from-orange-500 to-orange-600 border-orange-500/20",
                };

                return (
                  <Card key={index} className={`border-2 ${colorClasses[type.color as keyof typeof colorClasses].split(' ')[1]} hover:shadow-lg transition-all`}>
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-12 h-12 bg-gradient-to-br ${colorClasses[type.color as keyof typeof colorClasses].split(' ')[0]} rounded-lg flex items-center justify-center shadow-lg`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <CardTitle className="text-xl">{type.title}</CardTitle>
                      </div>
                      <CardDescription>{type.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <h4 className="font-bold mb-3 text-sm">주요 혜택</h4>
                      <div className="space-y-2">
                        {type.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-muted-foreground">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">파트너십 문의</h2>
              <p className="text-muted-foreground">아래 양식을 작성해주시면 담당자가 연락드리겠습니다</p>
            </div>

            <Card className="border-2 border-border">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">담당자명 *</Label>
                      <Input
                        id="name"
                        placeholder="홍길동"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">이메일 *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="example@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">회사명/기관명 *</Label>
                    <Input
                      id="company"
                      placeholder="(주)회사명"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="partnershipType">파트너십 유형 *</Label>
                    <Select 
                      value={formData.partnershipType} 
                      onValueChange={(value) => setFormData({...formData, partnershipType: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="파트너십 유형을 선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corporate">기업 파트너</SelectItem>
                        <SelectItem value="education">교육 기관</SelectItem>
                        <SelectItem value="global">글로벌 파트너</SelectItem>
                        <SelectItem value="technology">기술 파트너</SelectItem>
                        <SelectItem value="other">기타</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">문의 내용</Label>
                    <Textarea
                      id="message"
                      placeholder="파트너십에 대한 구체적인 제안이나 문의사항을 작성해주세요"
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                    >
                      문의 제출
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => navigate('/')}
                    >
                      취소
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Why Partner */}
            <div className="mt-12">
              <Card className="border-2 border-border bg-gradient-to-r from-blue-500/5 to-purple-500/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-blue-500" />
                    KAILA와 파트너가 되어야 하는 이유
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-bold mb-2">전문성</h4>
                      <p className="text-sm text-muted-foreground">
                        ISO/IEC 42001 기반의 검증된 진단 프레임워크
                      </p>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">성장성</h4>
                      <p className="text-sm text-muted-foreground">
                        빠르게 성장하는 AI 리터러시 시장 선점
                      </p>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">신뢰성</h4>
                      <p className="text-sm text-muted-foreground">
                        다수의 기업 진단 경험과 검증된 방법론
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold">KAILA</span>
                    <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  기업의 AI 리터러시 수준을 과학적으로 진단하고<br />
                  체계적으로 개선하는 전문 프레임워크
                </p>
              </div>

              <div>
                <h3 className="font-bold mb-4">프레임워크</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    홈
                  </button>
                  <button onClick={() => navigate('/about')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    소개
                  </button>
                  <button onClick={() => navigate('/methodology')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    방법론
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-4">문의</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/consultation')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    상담 신청
                  </button>
                  <button onClick={() => navigate('/resources')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    자료 요청
                  </button>
                  <button onClick={() => navigate('/partnership')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    파트너십
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  © 2025 KAILA Framework. All rights reserved.
                </p>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <button onClick={() => navigate('/admin/login')} className="hover:text-foreground transition-colors">
                    관리자 로그인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Partnership;
